/**
 * Cliente tipado: o caminho é a chave de tudo.
 *
 *     api.get("/transaction-certificates/{cert_id}/proposed-lots", { path: { cert_id } })
 *     api.get("/certificate-requests", { query: { estado: "pendente" } })
 *     api.patch("/certificate-requests/{request_id}", { path: { request_id }, body })
 *
 * O caminho decide, sozinho, o tipo da resposta, o do corpo, os parâmetros de
 * caminho e os de query. Já não há uma string de URL e, ao lado, um tipo que a
 * pessoa jura ser o mesmo: um caminho que o backend não tem, um parâmetro com o
 * nome errado ou uma query que não existe deixam de compilar.
 *
 * Fica POR CIMA de `apiRequest` — não o substitui. A renovação da sessão, as
 * repetições durante um deploy e o tratamento de erros (`lib/api.ts`) são
 * exatamente os mesmos; isto só constrói o URL e tipa o resultado.
 *
 * Uploads e downloads também passam por aqui, com o mesmo contrato de caminho:
 *
 *     api.upload("/process-maps/{map_id}/attachments", { path: { map_id }, form, onProgress })
 *     api.blob("/procedures/attachments/{attachment_id}/download", { path: { attachment_id } })
 *
 * O corpo `multipart` não está no contrato como JSON, por isso `upload` recebe um
 * `FormData` (`form`) e tipa a resposta pelo caminho; `blob` devolve o `Blob`
 * (o corpo de um erro também vem em blob: `blobErrorMessage` lê-o).
 */

import { apiRequest } from "@/lib/api";
import type { ApiPath, ApiPathWith, BodyOf, Metodo, Operacao, SuccessOf } from "@/types/api";

type Valor = string | number | boolean;

type Parametros<O> = O extends { parameters: infer P } ? P : never;
type DeCaminho<O> = Parametros<O> extends { path: infer X } ? X : never;
type DeQuery<O> = Parametros<O> extends { query?: infer Q } ? NonNullable<Q> : never;

type TemChaves<T> = [T] extends [never] ? false : keyof T extends never ? false : true;

/** `path` é obrigatório quando o caminho tem `{parâmetros}`; `query` e `body` quando a rota os exige. */
type Opcoes<P extends ApiPath, M extends Metodo> = (TemChaves<DeCaminho<Operacao<P, M>>> extends true
  ? { path: DeCaminho<Operacao<P, M>> }
  : { path?: never }) &
  (TemChaves<DeQuery<Operacao<P, M>>> extends true ? { query?: DeQuery<Operacao<P, M>> } : { query?: never }) &
  ([BodyOf<P, M>] extends [never] ? { body?: never } : undefined extends BodyOf<P, M> ? { body?: BodyOf<P, M> } : { body: BodyOf<P, M> });

/** Os argumentos a seguir ao caminho: omitem-se quando nada é obrigatório. */
type Argumentos<P extends ApiPath, M extends Metodo> = Record<never, never> extends Opcoes<P, M> ? [opcoes?: Opcoes<P, M>] : [opcoes: Opcoes<P, M>];

interface OpcoesEmRuntime {
  path?: Record<string, Valor>;
  query?: Record<string, Valor | readonly Valor[] | null | undefined>;
  body?: unknown;
}

/**
 * Constrói o URL (relativo a `/api/v1`) a partir do caminho do contrato.
 *
 * - `{param}` é substituído, com `encodeURIComponent`: um id com `/` ou `?` não
 *   escapa para outra rota.
 * - Na query, `undefined`, `null` e `""` omitem-se (um filtro por preencher, ou
 *   uma caixa de pesquisa vazia, não é «filtrar por vazio» — é a convenção de todos
 *   os hooks); `0` e `false` são valores e seguem. Listas repetem a chave
 *   (`a=1&a=2`), como o FastAPI lê.
 */
export function buildUrl(caminho: string, opcoes: Pick<OpcoesEmRuntime, "path" | "query"> = {}): string {
  const url = caminho.replace(/\{(\w+)\}/g, (_, nome: string) => {
    const valor = opcoes.path?.[nome];
    if (valor === undefined) throw new Error(`Falta o parâmetro de caminho «${nome}» em ${caminho}`);
    return encodeURIComponent(String(valor));
  });

  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(opcoes.query ?? {})) {
    if (valor === undefined || valor === null || valor === "") continue;
    for (const item of Array.isArray(valor) ? valor : [valor]) params.append(chave, String(item));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

const chamar =
  <M extends Metodo>(metodo: M) =>
  <P extends ApiPathWith<M>>(caminho: P, ...args: Argumentos<P, M>): Promise<SuccessOf<P, M>> => {
    const opcoes = (args[0] ?? {}) as OpcoesEmRuntime;
    return apiRequest<SuccessOf<P, M>>(buildUrl(caminho, opcoes), {
      method: metodo.toUpperCase(),
      body: opcoes.body,
    });
  };

type OpcoesDeUpload<P extends ApiPath> = Omit<Opcoes<P, "post">, "body"> & {
  form: FormData;
  /** 0–100; só é chamado quando o browser sabe o tamanho total. */
  onProgress?: (percent: number) => void;
};

const enviar = <P extends ApiPathWith<"post">>(caminho: P, opcoes: OpcoesDeUpload<P>): Promise<SuccessOf<P, "post">> => {
  const { form, onProgress } = opcoes as OpcoesDeUpload<P> & OpcoesEmRuntime;
  return apiRequest<SuccessOf<P, "post">>(buildUrl(caminho, opcoes as OpcoesEmRuntime), {
    method: "POST",
    body: form,
    onUploadProgress: onProgress
      ? (e) => {
          if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        }
      : undefined,
  });
};

const descarregar =
  <M extends "get" | "post">(metodo: M) =>
  <P extends ApiPathWith<M>>(caminho: P, ...args: Argumentos<P, M>): Promise<Blob> => {
    const opcoes = (args[0] ?? {}) as OpcoesEmRuntime;
    return apiRequest<Blob>(buildUrl(caminho, opcoes), { method: metodo.toUpperCase(), body: opcoes.body, responseType: "blob" });
  };

export const api = {
  get: chamar("get"),
  post: chamar("post"),
  put: chamar("put"),
  patch: chamar("patch"),
  delete: chamar("delete"),
  upload: enviar,
  blob: descarregar("get"),
  /** Como `blob`, mas por `POST` (ficheiros gerados a pedido, p. ex. etiquetas que ficam registadas). */
  postBlob: descarregar("post"),
};
